import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';
import { usePermissions, useCanManageLeague } from '../../../contexts/PermissionsContext';
import { formatLocalDateRange, formatTimestampDate } from '../../../utils/scheduleFormat';

const StatusChip = ({ status }) => (
  <Chip
    label={status}
    color={status === 'Approved' ? 'success' : 'default'}
    size="small"
    clickable={false}
    onClick={() => {}}
  />
);

const WarningsChip = ({ count }) => (
  <Chip
    label={count}
    color={count > 0 ? 'warning' : 'default'}
    variant={count > 0 ? 'filled' : 'outlined'}
    size="small"
    clickable={false}
    onClick={() => {}}
  />
);

const plural = (count, singular) => `${count} ${count === 1 ? singular : `${singular}s`}`;

const includesRegularSeason = (draft) => draft.includeRegularSeason !== false;

// e.g. "40 games · 6 other (4 TBD)", or "6 other games (4 TBD)" without a regular season.
const describeDraftGames = (draft) => {
  const extra = draft.extraGameCount ?? 0;
  const tbd = draft.tbdGameCount ?? 0;
  const tbdSuffix = tbd > 0 ? ` (${tbd} TBD)` : '';
  if (!includesRegularSeason(draft)) return `${plural(extra, 'other game')}${tbdSuffix}`;
  const regular = plural(draft.totalGames ?? 0, 'game');
  return extra > 0 ? `${regular} · ${extra} other${tbdSuffix}` : regular;
};

const describeDraftSize = (draft) => (includesRegularSeason(draft)
  ? `${plural(draft.playableWeeks ?? 0, 'week')} · ${describeDraftGames(draft)} · ${plural(draft.teamCount ?? 0, 'team')}`
  : describeDraftGames(draft));

const ScheduleGeneratorListPage = () => {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isLoading: permissionsLoading } = usePermissions();
  const canManageLeague = useCanManageLeague(parseInt(leagueId, 10));

  const [league, setLeague] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [leagueData, draftData] = await Promise.all([
        unifiedApi.getLeagueById(leagueId),
        unifiedApi.getScheduleDrafts(leagueId),
      ]);
      setLeague(leagueData);
      setDrafts(draftData || []);
    } catch (err) {
      setError(err?.message || 'Failed to load schedule drafts.');
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    if (canManageLeague) loadData();
  }, [canManageLeague, loadData]);

  const openDraft = (draftId) => navigate(`/admin/leagues/${leagueId}/schedule-generator/${draftId}`);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError(null);
    try {
      await unifiedApi.deleteScheduleDraft(leagueId, deleteTarget.id);
      setSuccess(`Deleted "${deleteTarget.name}".`);
      setDeleteTarget(null);
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to delete the draft.');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const renderActions = (draft) => (
    <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end' }}>
      <Tooltip title="Open">
        <IconButton aria-label={`Open ${draft.name}`} onClick={() => openDraft(draft.id)}>
          <OpenIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title={draft.status === 'Approved' ? 'Approved schedules cannot be deleted' : 'Delete'}>
        <span>
          <IconButton
            aria-label={`Delete ${draft.name}`}
            onClick={() => setDeleteTarget(draft)}
            disabled={draft.status === 'Approved'}
          >
            <DeleteIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );

  const nameLink = (draft) => (
    <Link
      component={RouterLink}
      to={`/admin/leagues/${leagueId}/schedule-generator/${draft.id}`}
      underline="hover"
      sx={{ fontWeight: 600, wordBreak: 'break-word' }}
    >
      {draft.name}
    </Link>
  );

  const updated = (draft) => formatTimestampDate(draft.updatedAt || draft.createdAt);
  const dateRange = (draft) => formatLocalDateRange(draft.firstGameDate, draft.lastGameDate) || '–';

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (drafts.length === 0) {
      return (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No schedules yet. Create one to generate a balanced regular-season schedule, playoffs or other games.
          </Typography>
        </Paper>
      );
    }
    if (isMobile) {
      return (
        <Stack spacing={2}>
          {drafts.map((draft) => (
            <Paper key={draft.id} variant="outlined" sx={{ p: 2 }} data-testid="schedule-draft-card">
              <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ minWidth: 0 }}>{nameLink(draft)}</Box>
                <StatusChip status={draft.status} />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {describeDraftSize(draft)}
              </Typography>
              <Typography variant="body2">{dateRange(draft)}</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, alignItems: 'center', justifyContent: 'space-between' }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Warnings</Typography>
                  <WarningsChip count={draft.warningCount} />
                </Stack>
                {renderActions(draft)}
              </Stack>
              <Typography variant="caption" color="text.secondary">{`Updated ${updated(draft)}`}</Typography>
            </Paper>
          ))}
        </Stack>
      );
    }
    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label="Schedule drafts">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Weeks</TableCell>
              <TableCell align="right">Games</TableCell>
              <TableCell>Dates</TableCell>
              <TableCell align="center">Warnings</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {drafts.map((draft) => (
              <TableRow key={draft.id} hover>
                <TableCell>{nameLink(draft)}</TableCell>
                <TableCell><StatusChip status={draft.status} /></TableCell>
                <TableCell align="right">{includesRegularSeason(draft) ? draft.playableWeeks : '–'}</TableCell>
                <TableCell align="right">{describeDraftGames(draft)}</TableCell>
                <TableCell>{dateRange(draft)}</TableCell>
                <TableCell align="center"><WarningsChip count={draft.warningCount} /></TableCell>
                <TableCell>{updated(draft)}</TableCell>
                <TableCell align="right">{renderActions(draft)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (permissionsLoading) {
    return (
      <AdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  if (!canManageLeague) {
    return (
      <AdminLayout>
        <Alert severity="error">You are not authorized to manage this league&apos;s schedule.</Alert>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/admin/leagues/${leagueId}`)}
          sx={{ mb: 2 }}
        >
          Back to League Admin
        </Button>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' } }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              Schedule Generator
            </Typography>
            {league?.name && <Typography color="text.secondary">{league.name}</Typography>}
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openDraft('new')}
            fullWidth={isMobile}
          >
            New schedule
          </Button>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Generate balanced regular-season schedules and playoff or other one-off games, compare drafts, and approve one to publish its games.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {renderContent()}

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle>Delete schedule draft?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`"${deleteTarget?.name}" will be permanently deleted. Existing games are not affected.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
};

export default ScheduleGeneratorListPage;
